<script setup lang="ts">
import DSContainer from '~/components/ds/Container.vue'
import DSButton from '~/components/ds/Button.vue'
import DSInput from '~/components/ds/Input.vue'
import DSRatingStars from '~/components/ds/RatingStars.vue'
import type { ShopProduct, ProductInventoryResponse, ConditionGroup, DeviceSummary } from '~/composables/useShopAPI'
import type { ProductReview, ReviewEligibility } from '~/composables/useReviewsAPI'
import type { AffiliateProduct } from '~/composables/useAffiliateAPI'
import {
  SITE_URL,
  buildProductSchema,
  buildBreadcrumbSchema,
  serializeJsonLd,
} from '~/utils/schemaOrg'
import { effectiveSingleItemShippingFee } from '~/utils/commercePolicy'

const route = useRoute()
const localePath = useLocalePath()
const { t, locale } = useI18n()
const { success, error: errorToast } = useDSToast()
const { formatPrice, formatDate, getDiscount, getConditionColor } = useShopUtils()
const { fetchProductBySlug, fetchProduct, fetchProducts, fetchProductInventory, loading } = useShopAPI()
const { addItem } = useCartAPI()
const { isLoggedIn } = useAuth()
const { navigateToLogin } = useLoginRedirect()
const { translateTitle, translateDescription } = useShopI18n()

definePageMeta({
  layout: 'default'
})

// Product state
const inventoryError = ref(false)
const affiliateProduct = ref<AffiliateProduct | null>(null)
const affiliateApi = useAffiliateAPI()

async function loadAffiliateProduct() {
  affiliateProduct.value = null
  if (!isLoggedIn.value || !product.value?.id) return
  try {
    const profile = await affiliateApi.profile()
    if (profile?.status !== 'ACTIVE') return
    affiliateProduct.value = await affiliateApi.product(product.value.id)
  } catch {
    // Ordinary customers and inactive affiliates keep the normal product page.
    affiliateProduct.value = null
  }
}

// --- Product fetch (SSR-aware) ---
// Using `useAsyncData` so the product (and therefore the JSON-LD computed
// from it) is present in the SSR HTML — Bing/Baidu/social unfurlers don't
// execute JS, so client-only product loads would have left their crawlers
// without Product/Breadcrumb schemas. Inventory is also SSR-loaded because an
// exact weekly deal must render the same device, image and price before and after
// hydration.
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const slugParam = computed(() => route.params.slug as string)

async function loadProduct(slug: string): Promise<ShopProduct | null> {
  if (!slug) return null
  try {
    if (UUID_PATTERN.test(slug)) {
      return await fetchProduct(slug)
    }
    try {
      return await fetchProductBySlug(slug)
    } catch {
      return await fetchProduct(slug)
    }
  } catch {
    return null
  }
}

const { data: product } = await useAsyncData<ShopProduct | null>(
  () => `product:${slugParam.value}`,
  () => loadProduct(slugParam.value),
  { watch: [slugParam] }
)

const notFound = computed(() => product.value === null)
const weeklyDealIsActive = useWeeklyDealExpiry(computed(() => product.value?.activeWeeklyDeal))

// SSR reaches the backend reliably (via the public URL), so a null product means
// it genuinely does not exist → return a real 404 instead of a 200 shell, which
// Google flags as a soft 404. Runs on both SSR (Googlebot) and client navigation.
if (notFound.value) {
  throw createError({ statusCode: 404, statusMessage: 'Product not found', fatal: true })
}

// --- Reviews (model-level): SSR-fetched so aggregateRating/review land in the
// JSON-LD HTML for crawlers. Reviews attach to the device model (productId) and
// are shared across colour/storage variants. ---
const { fetchSummary, fetchReviews, fetchEligibility, submitReview } = useReviewsAPI()

const { data: reviewData, refresh: refreshReviews } = await useAsyncData(
  () => `reviews:${product.value?.productId ?? 'none'}`,
  async () => {
    const pid = product.value?.productId
    if (!pid) return { summary: { average: null as number | null, count: 0 }, reviews: [] as ProductReview[] }
    const [summary, page] = await Promise.all([
      fetchSummary(pid).catch(() => ({ average: null as number | null, count: 0 })),
      fetchReviews(pid, 0, 10).catch(() => ({ items: [] as ProductReview[], total: 0, page: 0, size: 10, totalPages: 0 })),
    ])
    return { summary, reviews: page.items }
  },
  { watch: [() => product.value?.productId] },
)

const ratingSummary = computed(() => {
  const s = reviewData.value?.summary
  // Coerce average defensively: if the backend ever serializes the BigDecimal
  // as a string, `.toFixed`/schema would break — Number() normalizes it.
  const avg = s?.average
  return {
    average: avg != null ? Number(avg) : null,
    count: s?.count ?? 0,
  }
})
const reviewList = computed<ProductReview[]>(() => reviewData.value?.reviews ?? [])

// Inventory / condition selection state. Exact inventory targeting participates in
// price and structured-data output, so this cannot be deferred until onMounted.
const { data: inventory } = await useAsyncData<ProductInventoryResponse | null>(
  () => `product-inventory:${product.value?.id ?? 'none'}`,
  async () => {
    if (!product.value || product.value.productType === 'ACCESSORY') return null
    try {
      inventoryError.value = false
      return await fetchProductInventory(product.value.id)
    } catch {
      inventoryError.value = true
      return null
    }
  },
  { watch: [() => product.value?.id] },
)
const selectedCondition = ref<string | null>(null)
const selectedDevice = ref<DeviceSummary | null>(null)
const inventorySelectionUnavailable = ref(false)

function syncInitialInventorySelection() {
  const groups = inventory.value?.conditions || []
  const requestedId = typeof route.query.inventory === 'string' ? route.query.inventory : null
  if (groups.length === 0) {
    inventorySelectionUnavailable.value = requestedId != null
    selectedCondition.value = null
    selectedDevice.value = null
    return
  }
  const findDevice = (id: string | null) => id
    ? groups.flatMap(group => group.devices.map(device => ({ group, device })))
        .find(candidate => candidate.device.id === id)
    : undefined
  const requested = findDevice(requestedId)
  inventorySelectionUnavailable.value = requestedId != null && requested == null
  // Generic catalogue navigation keeps the catalogue's from-price semantics.
  // Only an explicit, valid inventory query (used by offer CTAs) selects the deal target.
  const candidate = requested || { group: groups[0], device: groups[0].devices[0] }
  selectedCondition.value = candidate.group.conditionGrade
  selectedDevice.value = candidate.device || null
}

watch(
  [inventory, () => route.query.inventory],
  syncInitialInventorySelection,
  { immediate: true },
)

const currentConditionGroup = computed<ConditionGroup | null>(() => {
  if (!inventory.value || !selectedCondition.value) return null
  return inventory.value.conditions.find(c => c.conditionGrade === selectedCondition.value) ?? null
})

// Accessories are fungible counted stock: no per-unit device picker, just a
// quantity bounded by `stockQuantity`. Everything device-specific is gated on this.
const isAccessory = computed(() => product.value?.productType === 'ACCESSORY')

// `stockQuantity` is the authoritative count for accessories.
const accessoryStock = computed(() => product.value?.stockQuantity ?? 0)
const accessoryOutOfStock = computed(() => isAccessory.value && accessoryStock.value <= 0)

const hasInventory = computed(() =>
  !isAccessory.value && inventory.value != null && inventory.value.conditions.length > 0,
)

// Display price: selected device price or product template price
const displayPrice = computed(() => {
  if (selectedDevice.value) return deviceDisplayPrice(selectedDevice.value)
  if (!weeklyDealIsActive.value && product.value?.activeWeeklyDeal) {
    return product.value.activeWeeklyDeal.regularPrice
  }
  return product.value?.salePrice ?? 0
})

const weeklyDealApplies = computed(() => {
  if (!weeklyDealIsActive.value || !product.value?.activeWeeklyDeal) return false
  return selectedDevice.value ? selectedDevice.value.weeklyDealApplied : true
})

function deviceDisplayPrice(device: DeviceSummary): number {
  if (device.weeklyDealApplied && !weeklyDealIsActive.value) return device.regularPrice
  return device.salePrice
}

function conditionDisplayPrice(group: ConditionGroup): number {
  const prices = group.devices.map(deviceDisplayPrice)
  return prices.length > 0 ? Math.min(...prices) : group.fromPrice
}

const isDeviceMode = computed(() => hasInventory.value)

const quantity = ref(1)
// Deep-link support: `?tab=reviews` opens the Reviews tab directly (used by the
// post-delivery review-invitation email). Falls back to description otherwise.
const activeTab = ref(route.query.tab === 'reviews' ? 'reviews' : 'description')
const selectedImageIndex = ref(0)
const lightboxOpen = ref(false)
const cartAction = ref<'add' | 'buy' | null>(null)
const isWishlisted = ref(false)

const openLightbox = () => {
  if (displayImages.value.length === 0) return
  lightboxOpen.value = true
}

// Display images: device-specific images fallback to product template images
const displayImages = computed(() => {
  if (selectedDevice.value?.images?.length) {
    return selectedDevice.value.images
  }
  return product.value?.images ?? []
})

// --- SEO: title, description, OG, Twitter, canonical, hreflang, JSON-LD ---
const HREFLANG_MAP = {
  es: 'es-ES',
  en: 'en-US',
  pt: 'pt-PT',
  zh: 'zh-CN',
  fr: 'fr-FR',
} as const
type HreflangLocale = keyof typeof HREFLANG_MAP

const BRAND_DISPLAY: Record<string, string> = {
  apple: 'Apple',
  samsung: 'Samsung',
  motorola: 'Motorola',
  xiaomi: 'Xiaomi',
  huawei: 'Huawei',
}

const localizedTitle = computed(() => {
  if (!product.value) return ''
  return translateTitle(product.value.titleTranslations, product.value.title)
})

const localizedDescription = computed(() => {
  if (!product.value) return ''
  return translateDescription(product.value.descriptionTranslations, product.value.description) || ''
})

// Related products are discovery links, not a client-only decoration. Keeping
// them in SSR HTML gives crawlers and no-JS users the same contextual links.
const { data: relatedProductData } = await useAsyncData<ShopProduct[]>(
  () => `related-products:${product.value?.id ?? 'none'}:${locale.value}`,
  async () => {
    if (!product.value?.categoryCode) return []
    const result = await fetchProducts({ categoryCode: product.value.categoryCode, size: 5 })
    return result.items.filter(item => item.id !== product.value!.id).slice(0, 4)
  },
  { watch: [() => product.value?.id, locale] },
)
const relatedProducts = computed(() => relatedProductData.value ?? [])

const productTitle = computed(() =>
  localizedTitle.value ? `${localizedTitle.value} | MiDiGi.NET` : 'MiDiGi.NET'
)

const productDescription = computed(() => {
  const desc = richTextToPlainText(localizedDescription.value)
  if (!desc) return t('seo.shop.description') as string
  return desc.length > 160 ? `${desc.slice(0, 157)}...` : desc
})

const canonical = computed(() => {
  const slug = product.value?.slug || (route.params.slug as string)
  if (!slug) return SITE_URL
  return `${SITE_URL}${localePath(`/producto/${slug}`)}`
})

const primaryImage = computed(() => {
  const imgs = displayImages.value
  if (!imgs || imgs.length === 0) return null
  const first = imgs[0]
  if (!first) return null
  if (/^https?:\/\//i.test(first)) return first
  return `${SITE_URL}${first.startsWith('/') ? '' : '/'}${first}`
})

const brandDisplay = computed(() => {
  const code = product.value?.brandCode
  if (!code) return undefined
  const key = code.toLowerCase()
  return BRAND_DISPLAY[key] || code.charAt(0).toUpperCase() + code.slice(1)
})

const categoryDisplay = computed(() => {
  const code = product.value?.categoryCode
  if (!code) return undefined
  return code.charAt(0).toUpperCase() + code.slice(1).toLowerCase()
})

// Note: og:type='product' is valid OG (http://ogp.me/#type_product) but @nuxtjs/seo's
// useSeoMeta type only allows the OG core/music/video subset. We emit it via useHead's
// meta array below instead so the type stays narrow.
useSeoMeta({
  title: productTitle,
  description: productDescription,
  ogTitle: productTitle,
  ogDescription: productDescription,
  ogUrl: canonical,
  ogImage: () => primaryImage.value || undefined,
  twitterCard: 'summary_large_image',
  twitterTitle: productTitle,
  twitterDescription: productDescription,
  twitterImage: () => primaryImage.value || undefined,
})

// Build hreflang URLs by hand. We deliberately don't use switchLocalePath
// here: on dynamic routes it occasionally resolves before route params are
// populated and returns `/producto` (no slug), which triggers Vue Router
// "No match found for location with path" warnings. The locale prefix +
// slug layout is stable, so constructing it directly is both faster and
// quieter.
const hreflangLinks = computed(() => {
  const slug = product.value?.slug || (route.params.slug as string)
  if (!slug) return []
  const codes = Object.keys(HREFLANG_MAP) as HreflangLocale[]
  const pathFor = (code: HreflangLocale) =>
    code === 'es' ? `/producto/${slug}` : `/${code}/producto/${slug}`
  const links = codes.map(code => ({
    rel: 'alternate' as const,
    hreflang: HREFLANG_MAP[code] as string,
    href: `${SITE_URL}${pathFor(code)}`,
  }))
  // x-default points to the default (Spanish) version
  links.push({
    rel: 'alternate' as const,
    hreflang: 'x-default',
    href: `${SITE_URL}/producto/${slug}`,
  })
  return links
})

const productSchema = computed(() => {
  if (!product.value) return null
  const priceVal = displayPrice.value || product.value.salePrice || null
  // Use the product UUID as SKU: it's a stable merchant identifier of valid
  // length. The slug was rejected by Google ("Invalid string length in sku") —
  // it can run ~70 chars, beyond the schema sku limit.
  const sku = product.value.id
  return buildProductSchema({
    name: localizedTitle.value,
    description: richTextToPlainText(localizedDescription.value),
    images: displayImages.value || [],
    sku,
    mpn: product.value.modelCode || undefined,
    brandName: brandDisplay.value,
    categoryName: categoryDisplay.value,
    priceEUR: priceVal,
    shippingFeeEUR: product.value.shippingFee,
    shippingFreeThresholdEUR: product.value.shippingFreeThreshold,
    originalPriceEUR: product.value.originalPrice ?? null,
    inStock: product.value.productType === 'ACCESSORY'
      ? (product.value.stockQuantity ?? 0) > 0
      : (product.value.inventoryCount ?? 0) > 0,
    itemCondition: product.value.productType === 'DEVICE'
      ? 'https://schema.org/RefurbishedCondition'
      : undefined,
    canonical: canonical.value,
    ratingValue: ratingSummary.value.average,
    ratingCount: ratingSummary.value.count,
    reviews: reviewList.value.slice(0, 5).map(r => ({
      author: r.authorName || (t('productPage.reviews.anonymous') as string),
      rating: r.rating,
      title: r.title || undefined,
      body: r.body || undefined,
      datePublished: r.createdAt,
    })),
  })
})

const breadcrumbSchema = computed(() => {
  if (!product.value) return null
  const slug = product.value.slug || (route.params.slug as string)
  const items = [
    { name: t('productPage.breadcrumb.home') as string, url: `${SITE_URL}${localePath('/')}` },
    { name: t('productPage.breadcrumb.shop') as string, url: `${SITE_URL}${localePath('/shop')}` },
  ]
  if (categoryDisplay.value && product.value.categoryCode) {
    // Build the category URL by hand: localePath() URL-encodes its argument,
    // so passing '/shop?category=foo' would emit '/shop%3Fcategory=foo'.
    items.push({
      name: categoryDisplay.value,
      url: `${SITE_URL}${localePath('/shop')}?category=${encodeURIComponent(product.value.categoryCode)}`,
    })
  }
  items.push({
    name: localizedTitle.value,
    url: `${SITE_URL}${localePath(`/producto/${slug}`)}`,
  })
  return buildBreadcrumbSchema(items)
})

useHead({
  meta: [
    // og:type=product is not in @nuxtjs/seo's useSeoMeta type but is valid OG.
    { property: 'og:type', content: 'product' },
  ],
  link: () => [
    { rel: 'canonical', href: canonical.value },
    ...hreflangLinks.value,
  ],
  script: () => {
    const scripts: Array<{ type: string; innerHTML: string }> = []
    if (productSchema.value) {
      scripts.push({
        type: 'application/ld+json',
        innerHTML: serializeJsonLd(productSchema.value),
      })
    }
    if (breadcrumbSchema.value) {
      scripts.push({
        type: 'application/ld+json',
        innerHTML: serializeJsonLd(breadcrumbSchema.value),
      })
    }
    return scripts
  },
})

onMounted(loadAffiliateProduct)
watch([isLoggedIn, () => product.value?.id], loadAffiliateProduct)

const selectImage = (index: number) => {
  selectedImageIndex.value = index
}

const discountPercentage = computed(() => {
  const compareAt = weeklyDealApplies.value
    ? (selectedDevice.value?.regularPrice || product.value?.activeWeeklyDeal?.regularPrice)
    : product.value?.originalPrice
  if (!compareAt || !displayPrice.value) return 0
  return getDiscount(displayPrice.value, compareAt)
})

const compareAtPrice = computed(() => weeklyDealApplies.value
  ? (selectedDevice.value?.regularPrice || product.value?.activeWeeklyDeal?.regularPrice)
  : product.value?.originalPrice)

// Shipping line: null/0 fee → free shipping. Otherwise show the fee and, if a
// free-shipping threshold exists, the "spend X to ship free" hint.
const isFreeShipping = computed(() => {
  return effectiveSingleItemShippingFee(
    product.value?.shippingFee,
    product.value?.shippingFreeThreshold,
    displayPrice.value,
  ) <= 0
})
const shippingFreeThreshold = computed(() => product.value?.shippingFreeThreshold ?? null)

const tabs = computed(() => [
  { id: 'description', label: t('productPage.tabs.description') },
  { id: 'reviews', label: `${t('productPage.tabs.reviews')}(${ratingSummary.value.count})` },
])

// --- Review eligibility (client-only; auth is client-side) ---
// Decides whether the submit form shows at all: only verified buyers who haven't
// reviewed yet and aren't muted. Anonymous visitors see a login prompt; everyone
// can still read approved reviews above. Endpoint requires auth, so we only call
// it when logged in.
const reviewEligibility = ref<ReviewEligibility | null>(null)
const eligibilityLoaded = ref(false)

async function loadReviewEligibility() {
  const pid = product.value?.productId
  if (!isLoggedIn.value || !pid) {
    reviewEligibility.value = null
    eligibilityLoaded.value = true
    return
  }
  try {
    reviewEligibility.value = await fetchEligibility(pid)
  } catch {
    reviewEligibility.value = null
  } finally {
    eligibilityLoaded.value = true
  }
}

onMounted(loadReviewEligibility)
watch([isLoggedIn, () => product.value?.productId], loadReviewEligibility)

// --- Review submission (verified buyers only; backend enforces the purchase) ---
const reviewForm = reactive({ rating: 0, title: '', body: '' })
const submittingReview = ref(false)

const formatReviewDate = (iso: string) => {
  try {
    return new Date(iso).toLocaleDateString(locale.value, { year: 'numeric', month: 'short', day: 'numeric' })
  } catch {
    return ''
  }
}

async function handleSubmitReview() {
  if (reviewForm.rating < 1) {
    errorToast(t('productPage.reviews.form.ratingRequired') as string)
    return
  }
  if (!product.value) return
  submittingReview.value = true
  try {
    await submitReview({
      productId: product.value.productId,
      rating: reviewForm.rating,
      title: reviewForm.title || undefined,
      body: reviewForm.body || undefined,
    })
    success(t('productPage.reviews.form.success') as string)
    reviewForm.rating = 0
    reviewForm.title = ''
    reviewForm.body = ''
    // One review per buyer/model: flip eligibility so the form is replaced by the
    // "already reviewed" note (the new review is pending and won't show in the list).
    if (reviewEligibility.value) {
      reviewEligibility.value = { ...reviewEligibility.value, alreadyReviewed: true, canReview: false }
    }
    // Don't await: a refresh failure must not surface an error toast right
    // after the success toast (the new review is pending and won't show anyway).
    refreshReviews().catch(() => {})
  } catch (e) {
    errorToast(e instanceof Error ? e.message : (t('productPage.reviews.form.error') as string))
  } finally {
    submittingReview.value = false
  }
}

const highlights = computed(() => {
  // The warranty highlight is refurbished-device messaging; accessories keep
  // only the shipping + returns highlights.
  const base = [
    // 「优先送货」是通用价值徽章，与下方按模版动态显示的运费/免运费解耦，避免冲突
    { icon: 'i-lucide-truck', textKey: 'productPage.priorityShipping' },
    { icon: 'i-lucide-rotate-ccw', textKey: 'shopPage.trustBadges.returns' },
  ]
  if (isAccessory.value) return base
  return [{ icon: 'i-lucide-shield-check', textKey: 'shopPage.product.warranty' }, ...base]
})

const specifications = computed(() => {
  if (!product.value) return []
  const specs: Array<{ label: string; value: string }> = []
  if (isAccessory.value) {
    // Accessories: surface the category instead of device-only attributes.
    if (product.value.accessoryCategoryName) {
      specs.push({ label: t('shopPage.filters.accessoryCategory'), value: product.value.accessoryCategoryName })
    }
    return specs
  }
  if (product.value.storage) specs.push({ label: t('productPage.storage'), value: product.value.storage })
  if (product.value.color) specs.push({ label: t('productPage.color'), value: product.value.color })
  if (product.value.conditionGrade) specs.push({ label: t('shopPage.filters.condition'), value: t(`shopPage.conditions.${product.value.conditionGrade}`) })
  return specs
})

const selectCondition = (grade: string) => {
  selectedCondition.value = grade
  const group = inventory.value?.conditions.find(c => c.conditionGrade === grade)
  if (group && group.devices.length > 0) {
    selectedDevice.value = group.devices[0]
    void navigateTo({ query: { ...route.query, inventory: group.devices[0].id } }, { replace: true })
  } else {
    selectedDevice.value = null
  }
  selectedImageIndex.value = 0
}

const selectDevice = (device: DeviceSummary) => {
  selectedDevice.value = device
  selectedImageIndex.value = 0
  void navigateTo({ query: { ...route.query, inventory: device.id } }, { replace: true })
}

// Upper bound for the quantity stepper: accessory counted stock, or (for a
// non-inventory device template) the template's inventoryCount.
const maxQuantity = computed(() => {
  if (isAccessory.value) return Math.max(accessoryStock.value, 1)
  return product.value?.inventoryCount || 1
})

const incrementQuantity = () => {
  if (isDeviceMode.value) return
  if (quantity.value < maxQuantity.value) {
    quantity.value++
  }
}

const decrementQuantity = () => {
  if (isDeviceMode.value) return
  if (quantity.value > 1) {
    quantity.value--
  }
}

const addToCart = async () => {
  if (!isLoggedIn.value) {
    errorToast(t('productPage.loginRequired'))
    navigateToLogin()
    return
  }
  cartAction.value = 'add'
  try {
    if (isDeviceMode.value && selectedDevice.value) {
      await addItem(product.value!.id, 1, selectedDevice.value.id)
    } else {
      await addItem(product.value!.id, quantity.value)
    }
    success(t('productPage.addedToCart'))
  } catch (e) {
    errorToast(e instanceof Error ? e.message : t('productPage.addToCartError'))
  } finally {
    cartAction.value = null
  }
}

const buyNow = async () => {
  if (!isLoggedIn.value) {
    errorToast(t('productPage.loginRequired'))
    navigateToLogin()
    return
  }
  cartAction.value = 'buy'
  try {
    if (isDeviceMode.value && selectedDevice.value) {
      await addItem(product.value!.id, 1, selectedDevice.value.id)
    } else {
      await addItem(product.value!.id, quantity.value)
    }
    navigateTo(localePath('/carrito'))
  } catch (e) {
    errorToast(e instanceof Error ? e.message : t('productPage.addToCartError'))
  } finally {
    cartAction.value = null
  }
}

const formatWarrantyDate = (dateStr: string) => {
  return formatDate(dateStr)
}

const formatWeeklyDealDeadline = (dateStr: string) => formatDate(dateStr, true)

const toggleWishlist = () => {
  isWishlisted.value = !isWishlisted.value
  if (isWishlisted.value) {
    success(t('productPage.addedToWishlist'))
  }
}

</script>

<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-950">
    <!-- Loading -->
    <div v-if="loading && !product" class="flex items-center justify-center min-h-[60vh]">
      <div class="w-8 h-8 border-4 border-brand-600 border-t-transparent rounded-full animate-spin" />
    </div>

    <!-- Not Found -->
    <div v-else-if="notFound" class="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
      <UIcon name="i-lucide-search-x" class="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
      <h2 class="text-xl font-bold text-gray-900 dark:text-white mb-2">
        {{ t('productPage.notFound') }}
      </h2>
      <NuxtLink :to="localePath('/shop')" class="text-brand-600 dark:text-brand-400 hover:underline mt-4">
        {{ t('productPage.breadcrumb.shop') }}
      </NuxtLink>
    </div>

    <!-- Product Page -->
    <template v-else-if="product">
      <!-- Breadcrumb -->
      <div class="bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800">
        <DSContainer class="py-4">
          <nav class="flex items-center gap-2 text-sm">
            <NuxtLink :to="localePath('/')" class="text-gray-500 dark:text-gray-400 hover:text-brand-600 dark:hover:text-brand-400 transition-colors">
              {{ t('productPage.breadcrumb.home') }}
            </NuxtLink>
            <UIcon name="i-lucide-chevron-right" class="w-4 h-4 text-gray-400" />
            <NuxtLink :to="localePath('/shop')" class="text-gray-500 dark:text-gray-400 hover:text-brand-600 dark:hover:text-brand-400 transition-colors">
              {{ t('productPage.breadcrumb.shop') }}
            </NuxtLink>
            <UIcon name="i-lucide-chevron-right" class="w-4 h-4 text-gray-400" />
            <span class="text-gray-900 dark:text-white font-medium truncate max-w-[200px]">{{ translateTitle(product.titleTranslations, product.title) }}</span>
          </nav>
        </DSContainer>
      </div>

      <DSContainer class="py-8">
        <!-- Main Product Section -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mb-16">
          <!-- Product Images -->
          <div class="space-y-4">
            <!-- Main Image -->
            <div class="relative aspect-square bg-white dark:bg-gray-900 rounded-2xl overflow-hidden border border-gray-100 dark:border-gray-800 group">
              <!-- Badges -->
              <div class="absolute top-4 left-4 z-10 flex flex-col gap-2">
                <span v-if="discountPercentage > 0" class="bg-green-500 text-white text-sm font-bold px-3 py-1 rounded-full shadow-lg">
                  -{{ discountPercentage }}%
                </span>
              </div>

              <!-- Wishlist Button -->
              <button
                class="absolute top-4 right-4 z-10 w-10 h-10 bg-white dark:bg-gray-800 rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
                @click="toggleWishlist"
              >
                <UIcon
                  name="i-lucide-heart"
                  :class="['w-5 h-5 transition-colors', isWishlisted ? 'text-red-500 fill-red-500' : 'text-gray-400']"
                />
              </button>

              <!-- Main Image (click to enlarge) -->
              <button
                v-if="displayImages.length > 0"
                type="button"
                class="absolute inset-0 w-full h-full cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                :aria-label="t('productPage.lightbox.open')"
                @click="openLightbox"
              >
                <img
                  :src="imageUrl(displayImages[selectedImageIndex] || displayImages[0], { w: 800, h: 800, fit: 'contain' })"
                  :alt="translateTitle(product.titleTranslations, product.title)"
                  width="800"
                  height="800"
                  fetchpriority="high"
                  class="w-full h-full object-contain p-8"
                >
                <!-- Zoom hint (appears on hover) -->
                <div class="absolute bottom-3 right-3 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/70 text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none backdrop-blur-sm">
                  <UIcon name="i-lucide-zoom-in" class="w-3.5 h-3.5" />
                  <span>{{ t('productPage.lightbox.open') }}</span>
                </div>
              </button>
              <div v-else class="absolute inset-0 flex items-center justify-center">
                <UIcon name="i-lucide-smartphone" class="w-48 h-48 text-gray-200 dark:text-gray-700" />
              </div>
            </div>

            <!-- Thumbnail Images -->
            <div v-if="displayImages.length > 1" class="grid grid-cols-4 gap-3">
              <button
                v-for="(image, index) in displayImages"
                :key="index"
                :class="[
                  'relative aspect-square bg-white dark:bg-gray-900 rounded-xl overflow-hidden border-2 transition-all',
                  selectedImageIndex === index
                    ? 'border-brand-500 shadow-md'
                    : 'border-gray-100 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-600'
                ]"
                @click="selectImage(index)"
              >
                <img :src="imageUrl(image, { w: 120, h: 120 })" :alt="`${translateTitle(product.titleTranslations, product.title)} - ${index + 1}`" width="120" height="120" loading="lazy" class="w-full h-full object-contain p-2">
              </button>
            </div>
          </div>

          <!-- Product Info -->
          <div class="space-y-6">
            <!-- Category -->
            <div class="flex items-center justify-between">
              <NuxtLink
                :to="localePath('/shop')"
                class="text-sm font-medium text-brand-600 dark:text-brand-400 hover:underline"
              >
                {{ product.categoryCode }}
              </NuxtLink>
              <div v-if="isAccessory ? !accessoryOutOfStock : product.inventoryCount > 0" class="flex items-center gap-1 text-sm text-green-600 dark:text-green-400">
                <UIcon name="i-lucide-check-circle" class="w-4 h-4" />
                <span>{{ t('productPage.inStock') }}</span>
              </div>
            </div>

            <!-- Product Title -->
            <h1 class="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white leading-tight">
              {{ translateTitle(product.titleTranslations, product.title) }}
            </h1>

            <div
              v-if="inventorySelectionUnavailable"
              role="status"
              class="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
            >
              <UIcon name="i-lucide-triangle-alert" class="mt-0.5 size-4 shrink-0" />
              <span>{{ t('weeklyDeal.inventoryUnavailable') }}</span>
            </div>

            <!-- Price Section -->
            <div class="bg-gradient-to-r from-brand-50 to-indigo-50 dark:from-brand-900/20 dark:to-indigo-900/20 rounded-2xl p-6 border border-brand-100 dark:border-brand-800/50">
              <div class="flex items-baseline gap-3 mb-2">
                <span class="text-4xl font-bold text-gray-900 dark:text-white">
                  {{ formatPrice(displayPrice) }}
                </span>
                <span v-if="compareAtPrice" class="text-xl text-gray-400 line-through">
                  {{ formatPrice(compareAtPrice) }}
                </span>
                <span v-if="discountPercentage > 0" class="text-sm font-semibold text-green-600 dark:text-green-400">
                  {{ t('productPage.youSave') }} {{ formatPrice(compareAtPrice - displayPrice) }}
                </span>
              </div>
              <div v-if="product.activeWeeklyDeal && weeklyDealApplies" class="mt-3 flex items-center gap-2 text-sm font-semibold text-orange-700 dark:text-orange-300">
                <UIcon name="i-lucide-flame" class="h-4 w-4" />
                <span>{{ t('weeklyDeal.productNotice') }}</span>
                <span class="font-normal">{{ t('weeklyDeal.until', { date: formatWeeklyDealDeadline(product.activeWeeklyDeal.endsAt) }) }}</span>
              </div>
              <FinancingNotice v-if="displayPrice > 0" class="mt-4 border-t border-brand-100 pt-4 dark:border-brand-800/50" />
            </div>

            <AffiliateProductShareTools
              v-if="affiliateProduct"
              :product="affiliateProduct"
              :product-title="translateTitle(product.titleTranslations, product.title)"
              :display-price="displayPrice"
            />

            <!-- Highlights Grid -->
            <div class="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div
                v-for="highlight in highlights"
                :key="highlight.textKey"
                class="flex items-center gap-2 bg-white dark:bg-gray-900 rounded-xl p-3 border border-gray-100 dark:border-gray-800"
              >
                <div class="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                  <UIcon :name="highlight.icon" class="w-4 h-4 text-green-600 dark:text-green-400" />
                </div>
                <span class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ t(highlight.textKey) }}</span>
              </div>
            </div>

            <!-- Product Details (storage, color) -->
            <div v-if="product.storage || product.color" class="flex flex-wrap gap-3">
              <div v-if="product.storage" class="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 rounded-xl px-4 py-2">
                <span class="text-sm text-gray-500 dark:text-gray-400">{{ t('productPage.storage') }}:</span>
                <span class="text-sm font-medium text-gray-900 dark:text-white">{{ product.storage }}</span>
              </div>
              <div v-if="product.color" class="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 rounded-xl px-4 py-2">
                <span class="text-sm text-gray-500 dark:text-gray-400">{{ t('productPage.color') }}:</span>
                <span class="text-sm font-medium text-gray-900 dark:text-white">{{ product.color }}</span>
              </div>
            </div>

            <!-- Accessory category (replaces device storage/color/condition) -->
            <div v-if="isAccessory && product.accessoryCategoryName" class="flex flex-wrap gap-3">
              <div class="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 rounded-xl px-4 py-2">
                <UIcon name="i-lucide-cable" class="w-4 h-4 text-gray-500 dark:text-gray-400" />
                <span class="text-sm text-gray-500 dark:text-gray-400">{{ t('shopPage.filters.accessoryCategory') }}:</span>
                <span class="text-sm font-medium text-gray-900 dark:text-white">{{ product.accessoryCategoryName }}</span>
              </div>
            </div>

            <!-- Condition Selector + Device Picker (when inventory available) -->
            <template v-if="hasInventory">
              <!-- Condition Grade Selector -->
              <div class="space-y-3">
                <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                  {{ t('productPage.selectCondition') }}
                </h3>
                <div class="flex flex-wrap gap-2">
                  <button
                    v-for="group in inventory!.conditions"
                    :key="group.conditionGrade"
                    :class="[
                      'px-4 py-2 rounded-xl text-sm font-medium border-2 transition-all',
                      selectedCondition === group.conditionGrade
                        ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20 text-brand-700 dark:text-brand-300 shadow-sm'
                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                    ]"
                    @click="selectCondition(group.conditionGrade)"
                  >
                    <span :class="['inline-block w-2 h-2 rounded-full mr-2', getConditionColor(group.conditionGrade).split(' ')[0]]" />
                    {{ t(`shopPage.conditions.${group.conditionGrade}`) }}
                    <span class="text-xs text-gray-400 dark:text-gray-500 ml-1">
                      {{ t('productPage.conditionFrom', { price: formatPrice(conditionDisplayPrice(group)) }) }}
                    </span>
                  </button>
                </div>
              </div>

              <!-- Device List -->
              <div v-if="currentConditionGroup" class="space-y-3">
                <div class="flex items-center justify-between">
                  <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                    {{ t('productPage.selectDevice') }}
                  </h3>
                  <span class="text-xs text-gray-500 dark:text-gray-400">
                    {{ t('productPage.devicesAvailable', { count: currentConditionGroup.count }) }}
                  </span>
                </div>
                <div class="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden divide-y divide-gray-100 dark:divide-gray-800">
                  <button
                    v-for="device in currentConditionGroup.devices"
                    :key="device.id"
                    :class="[
                      'w-full flex items-center gap-4 px-4 py-3 text-left transition-colors',
                      selectedDevice?.id === device.id
                        ? 'bg-brand-50 dark:bg-brand-900/20'
                        : 'bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                    ]"
                    @click="selectDevice(device)"
                  >
                    <!-- Check indicator -->
                    <div :class="[
                      'w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0',
                      selectedDevice?.id === device.id
                        ? 'border-brand-500 bg-brand-500'
                        : 'border-gray-300 dark:border-gray-600'
                    ]">
                      <UIcon v-if="selectedDevice?.id === device.id" name="i-lucide-check" class="w-3 h-3 text-white" />
                    </div>

                    <!-- Price and exact-device deal marker -->
                    <div class="flex min-w-[150px] flex-wrap items-center gap-2">
                      <span class="text-lg font-bold text-gray-900 dark:text-white">
                        {{ formatPrice(deviceDisplayPrice(device)) }}
                      </span>
                      <span
                        v-if="device.weeklyDealApplied && weeklyDealIsActive"
                        class="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-700 dark:bg-orange-900/40 dark:text-orange-300"
                      >
                        <UIcon name="i-lucide-flame" class="size-3" />
                        {{ t('weeklyDeal.badge') }}
                      </span>
                    </div>

                    <!-- Device details -->
                    <div class="flex flex-wrap items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                      <span v-if="device.batteryHealth != null" class="flex items-center gap-1">
                        <UIcon name="i-lucide-battery-medium" class="w-4 h-4" />
                        {{ t('productPage.batteryHealth', { percent: device.batteryHealth }) }}
                      </span>
                      <span v-if="device.storage" class="flex items-center gap-1">
                        <UIcon name="i-lucide-hard-drive" class="w-4 h-4" />
                        {{ device.storage }}
                      </span>
                      <span v-if="device.color" class="flex items-center gap-1">
                        <UIcon name="i-lucide-palette" class="w-4 h-4" />
                        {{ device.color }}
                      </span>
                      <span v-if="device.warrantyExpiresAt" class="flex items-center gap-1">
                        <UIcon name="i-lucide-clock" class="w-4 h-4" />
                        {{ t('productPage.factoryWarrantyUntil', { date: formatWarrantyDate(device.warrantyExpiresAt) }) }}
                      </span>
                    </div>
                  </button>
                </div>
              </div>
            </template>

            <!-- Inventory Load Error -->
            <div v-if="inventoryError" class="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800/50 rounded-xl p-4 flex items-center gap-3">
              <UIcon name="i-lucide-alert-triangle" class="w-5 h-5 text-yellow-500 flex-shrink-0" />
              <span class="text-sm text-yellow-700 dark:text-yellow-300">{{ t('productPage.inventoryError', 'Could not load inventory. Please try again later.') }}</span>
            </div>

            <!-- Sold Out: accessories key on stockQuantity; devices on inventoryCount -->
            <div v-if="isAccessory ? accessoryOutOfStock : product.inventoryCount === 0" class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-xl p-6 flex items-center justify-center gap-3">
              <UIcon name="i-lucide-package-x" class="w-6 h-6 text-red-500" />
              <span class="text-lg font-semibold text-red-600 dark:text-red-400">{{ t('productPage.soldOut') }}</span>
            </div>

            <!-- Quantity & Add to Cart (only when in stock) -->
            <div v-else class="flex flex-col sm:flex-row gap-4">
              <!-- Quantity (hidden in device picker mode; shown for accessories + plain device templates) -->
              <div v-if="!isDeviceMode" class="flex items-center border-2 border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                <button
                  class="w-12 h-12 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  :disabled="quantity <= 1"
                  :aria-label="t('productPage.decreaseQuantity')"
                  @click="decrementQuantity"
                >
                  <UIcon name="i-lucide-minus" class="w-5 h-5 text-gray-700 dark:text-gray-300" />
                </button>
                <input
                  v-model="quantity"
                  type="number"
                  min="1"
                  :max="maxQuantity"
                  :aria-label="t('cartPage.items.quantity')"
                  class="w-16 text-center bg-transparent text-gray-900 dark:text-white font-semibold text-lg focus:outline-none"
                  readonly
                >
                <button
                  class="w-12 h-12 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  :disabled="quantity >= maxQuantity"
                  :aria-label="t('productPage.increaseQuantity')"
                  @click="incrementQuantity"
                >
                  <UIcon name="i-lucide-plus" class="w-5 h-5 text-gray-700 dark:text-gray-300" />
                </button>
              </div>

              <!-- Add to Cart Button (device-variant gating only applies to the device picker) -->
              <button
                class="flex-1 bg-brand-600 hover:bg-brand-700 disabled:bg-brand-400 text-white py-4 px-6 rounded-xl font-semibold text-lg transition-colors flex items-center justify-center gap-2"
                :disabled="cartAction !== null || (isDeviceMode && !selectedDevice)"
                @click="addToCart"
              >
                <div v-if="cartAction === 'add'" class="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <template v-else>
                  <UIcon name="i-lucide-shopping-cart" class="w-5 h-5" />
                  {{ t('productPage.addToCart') }}
                </template>
              </button>

              <!-- Buy Now Button -->
              <button
                class="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white py-4 px-6 rounded-xl font-semibold text-lg transition-colors flex items-center justify-center gap-2"
                :disabled="cartAction !== null || (isDeviceMode && !selectedDevice)"
                @click="buyNow"
              >
                <div v-if="cartAction === 'buy'" class="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <template v-else>
                  <UIcon name="i-lucide-zap" class="w-5 h-5" />
                  {{ t('productPage.buyNow') }}
                </template>
              </button>
            </div>

            <!-- Shipping Info -->
            <div class="bg-white dark:bg-gray-900 rounded-2xl p-4 border border-gray-100 dark:border-gray-800">
              <div class="flex items-center gap-3">
                <div class="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <UIcon name="i-lucide-truck" class="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p class="font-semibold text-gray-900 dark:text-white">
                    <template v-if="isFreeShipping">{{ t('productPage.freeShipping') }}</template>
                    <template v-else>{{ t('productPage.shippingFee', { price: formatPrice(product.shippingFee) }) }}</template>
                  </p>
                  <p v-if="!isFreeShipping && shippingFreeThreshold" class="text-xs text-green-600 dark:text-green-400 mt-0.5">
                    {{ t('productPage.shippingFreeOver', { threshold: formatPrice(shippingFreeThreshold) }) }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Trust Badges -->
            <div class="flex flex-wrap gap-4 py-4 border-t border-gray-100 dark:border-gray-800">
              <div class="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <UIcon name="i-lucide-shield-check" class="w-5 h-5 text-green-500" />
                <span>{{ t('productPage.securePayment') }}</span>
              </div>
              <div v-if="!isAccessory" class="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <UIcon name="i-lucide-award" class="w-5 h-5 text-blue-500" />
                <span>{{ t('productPage.certifiedDevice') }}</span>
              </div>
              <div class="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <UIcon name="i-lucide-headphones" class="w-5 h-5 text-purple-500" />
                <span>{{ t('productPage.support') }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Tabs Section -->
        <div class="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden mb-16">
          <!-- Tab Headers -->
          <div class="flex border-b border-gray-100 dark:border-gray-800">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              :class="[
                'px-6 py-4 text-sm font-semibold transition-colors relative',
                activeTab === tab.id
                  ? 'text-brand-600 dark:text-brand-400'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              ]"
              @click="activeTab = tab.id"
            >
              {{ tab.label }}
              <div
                v-if="activeTab === tab.id"
                class="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-600 dark:bg-brand-400"
              />
            </button>
          </div>

          <!-- Tab Content -->
          <div class="p-6 lg:p-8">
            <!-- Description Tab -->
            <RichContentRenderer
              v-if="activeTab === 'description'"
              :content="translateDescription(product.descriptionTranslations, product.description)"
            />

            <!-- Specifications Tab -->
            <div v-else-if="activeTab === 'specifications'">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div
                  v-for="spec in specifications"
                  :key="spec.label"
                  class="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-800"
                >
                  <span class="text-gray-500 dark:text-gray-400">{{ spec.label }}</span>
                  <span class="font-medium text-gray-900 dark:text-white">{{ spec.value }}</span>
                </div>
              </div>
            </div>

            <!-- Reviews Tab -->
            <div v-else-if="activeTab === 'reviews'" class="space-y-8">
              <!-- Summary -->
              <div class="flex items-center gap-4">
                <div class="text-4xl font-bold text-gray-900 dark:text-white">
                  {{ ratingSummary.average != null ? ratingSummary.average.toFixed(1) : '—' }}
                </div>
                <div>
                  <DSRatingStars :model-value="ratingSummary.average ?? 0" size-class="w-5 h-5" />
                  <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {{ t('productPage.reviews.totalReviews', { count: ratingSummary.count }) }}
                  </p>
                </div>
              </div>

              <!-- List -->
              <div v-if="reviewList.length > 0" class="space-y-6">
                <div
                  v-for="review in reviewList"
                  :key="review.id"
                  class="border-b border-gray-100 dark:border-gray-800 pb-6 last:border-0"
                >
                  <div class="flex items-center justify-between mb-2">
                    <DSRatingStars :model-value="review.rating" size-class="w-4 h-4" />
                    <span class="text-xs text-gray-400">{{ formatReviewDate(review.createdAt) }}</span>
                  </div>
                  <p v-if="review.title" class="font-semibold text-gray-900 dark:text-white">{{ review.title }}</p>
                  <p v-if="review.body" class="text-gray-600 dark:text-gray-300 mt-1 whitespace-pre-line">{{ review.body }}</p>
                  <p class="text-sm text-gray-400 mt-2">
                    {{ review.authorName || t('productPage.reviews.anonymous') }}
                    <span class="text-green-600 dark:text-green-400">· {{ t('productPage.reviews.verified') }}</span>
                  </p>
                </div>
              </div>
              <p v-else class="text-gray-500 dark:text-gray-400">{{ t('productPage.reviews.empty') }}</p>

              <!-- Submit gate: only verified buyers who haven't reviewed yet see the form -->
              <!-- Not logged in → prompt to log in -->
              <div
                v-if="!isLoggedIn"
                class="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
              >
                <p class="text-sm text-gray-600 dark:text-gray-300">{{ t('productPage.reviews.loginPrompt') }}</p>
                <DSButton variant="primary" @click="navigateToLogin">
                  {{ t('productPage.reviews.loginCta') }}
                </DSButton>
              </div>

              <!-- Logged in + eligible → write a review -->
              <div
                v-else-if="reviewEligibility?.canReview"
                class="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-6"
              >
                <h3 class="font-semibold text-gray-900 dark:text-white mb-3">{{ t('productPage.reviews.form.title') }}</h3>

                <!-- The product being reviewed -->
                <div class="flex items-center gap-3 mb-4 p-3 rounded-lg bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800">
                  <img
                    v-if="displayImages.length > 0"
                    :src="imageUrl(displayImages[0], { w: 96, h: 96, fit: 'contain' })"
                    :alt="localizedTitle"
                    width="48"
                    height="48"
                    class="w-12 h-12 rounded object-contain bg-gray-50 dark:bg-gray-800 shrink-0"
                  >
                  <div class="min-w-0">
                    <p class="text-xs text-gray-500 dark:text-gray-400">{{ t('productPage.reviews.form.reviewingLabel') }}</p>
                    <p class="text-sm font-medium text-gray-900 dark:text-white truncate">{{ localizedTitle }}</p>
                  </div>
                </div>

                <DSRatingStars v-model="reviewForm.rating" interactive size-class="w-7 h-7" class="mb-4" />

                <DSInput
                  v-model="reviewForm.title"
                  :placeholder="t('productPage.reviews.form.titlePlaceholder')"
                  class="mb-3"
                />
                <textarea
                  v-model="reviewForm.body"
                  :placeholder="t('productPage.reviews.form.bodyPlaceholder')"
                  rows="4"
                  class="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 mb-4"
                />
                <DSButton
                  variant="primary"
                  :loading="submittingReview"
                  @click="handleSubmitReview"
                >
                  {{ t('productPage.reviews.form.submit') }}
                </DSButton>
              </div>

              <!-- Logged in, already reviewed → no second review -->
              <div
                v-else-if="reviewEligibility?.alreadyReviewed"
                class="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300"
              >
                <UIcon name="i-lucide-check-circle" class="w-5 h-5 text-green-600 dark:text-green-400 shrink-0" />
                {{ t('productPage.reviews.alreadyReviewed') }}
              </div>

              <!-- Logged in, muted by admin -->
              <div
                v-else-if="reviewEligibility?.blocked"
                class="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300"
              >
                <UIcon name="i-lucide-message-square-x" class="w-5 h-5 text-gray-400 shrink-0" />
                {{ t('productPage.reviews.blockedNote') }}
              </div>

              <!-- Logged in, hasn't purchased → read-only (no form) -->
              <p
                v-else-if="eligibilityLoaded && reviewEligibility && !reviewEligibility.purchased"
                class="text-sm text-gray-500 dark:text-gray-400"
              >
                {{ t('productPage.reviews.purchaseRequired') }}
              </p>
            </div>
          </div>
        </div>

        <!-- Related Products -->
        <div v-if="relatedProducts.length > 0">
          <div class="flex items-center justify-between mb-8">
            <h2 class="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
              {{ t('productPage.relatedProducts') }}
            </h2>
            <NuxtLink
              :to="localePath('/shop')"
              class="text-brand-600 dark:text-brand-400 hover:underline font-medium flex items-center gap-1"
            >
              {{ t('productPage.viewAll') }}
              <UIcon name="i-lucide-arrow-right" class="w-4 h-4" />
            </NuxtLink>
          </div>

          <div class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <ProductCard
              v-for="relatedProduct in relatedProducts"
              :key="relatedProduct.id"
              :product="relatedProduct"
              :image-size="300"
            />
          </div>
        </div>
      </DSContainer>
    </template>

    <!-- Image lightbox (cover-click zoom) -->
    <ProductImageLightbox
      v-if="product && displayImages.length > 0"
      v-model="lightboxOpen"
      :images="displayImages"
      :initial-index="selectedImageIndex"
      :alt="translateTitle(product.titleTranslations, product.title)"
      @update:index="selectedImageIndex = $event"
    />
  </div>
</template>
